"use client";

import { SafeListing, SafeUser } from "@/app/types";
import { getAttribution } from "@/app/libs/attribution";
import ListingCard from "@/app/components/listings/ListingCard";
import Pagination from "@/app/components/Pagination";
import { useRouter, useSearchParams } from "next/navigation";
import FilterMenuState from "@/app/components/FilterMenuState";
import { IListingsParams } from "@/app/actions/getListings";
import { ExchangeCurrency } from "@prisma/client";
import qs from "query-string";
import { useState, useEffect, useMemo } from "react";
import { HiOutlineMap, HiOutlineXMark } from "react-icons/hi2";
import dynamic from "next/dynamic";

const MapListings = dynamic(() => import('../components/MapListings'), {
    ssr: false,
    loading: () => <div className="w-full h-full bg-gray-200 animate-pulse rounded-lg"></div>
});

interface SearchClientProps {
    listings: SafeListing[];
    currentUser?: SafeUser | null;
    totalPages: number;
    currentPage: number;
    searchParams: IListingsParams;
    rates: ExchangeCurrency | null;
    isSearch?: boolean;
}

const SearchClient: React.FC<SearchClientProps> = ({
    listings,
    currentUser,
    totalPages,
    currentPage,
    searchParams,
    rates,
    isSearch
}) => {
    const router = useRouter();
    const params = useSearchParams();
    // Clicks are only comparable per destination search — home browsing is not tracked.
    const locationQuery = String(
        searchParams.cityOrCountry || searchParams.city || searchParams.country || ""
    ).trim();

    const trackResultClick = (listingId: string, position: number) => {
        try {
            fetch("/api/track", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    type: "search_click",
                    listingId,
                    metadata: {
                        cityOrCountry: locationQuery,
                        resultType: "listing",
                        position,
                    },
                    attribution: getAttribution(),
                }),
                keepalive: true,
            }).catch(() => {
                // Silent fail to avoid affecting UX
            });
        } catch {
            // Silent fail to avoid affecting UX
        }
    };
    const [showMap, setShowMap] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [visibleListings, setVisibleListings] = useState<SafeListing[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        setIsLoading(true);

        const initialListings = listings.slice(0, 5);
        setVisibleListings(initialListings);

        if (listings.length > 5) {
            const timer = setTimeout(() => {
                setVisibleListings(listings);
                setIsLoading(false);
            }, 100);

            return () => clearTimeout(timer);
        } else {
            setIsLoading(false);
        }
    }, [listings]);

    const onPageChange = (page: number) => {
        const current = qs.parse(params?.toString() || '');
        const query = {
            ...current,
            page: page
        };

        const url = qs.stringifyUrl({
            url: isSearch ? '/search' : '/',
            query
        }, { skipNull: true });

        router.push(url);
    };

    const handleShowMap = () => {
        setShowMap(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleHideMap = () => {
        setShowMap(false);
    };

    const gridClasses = useMemo(() => `
        grid grid-cols-2 sm:grid-cols-3 
        ${showMap ? 'lg:grid-cols-3' : 'lg:grid-cols-5'}
        gap-6 bg-white
    `, [showMap]);

    return (
        <div className="relative z-0 mx-4">
            {listings.length > 0 && (
                <FilterMenuState
                    listings={listings}
                    rates={rates}
                />
            )}

            {listings.length > 0 && (
                <button
                    type="button"
                    onClick={showMap ? handleHideMap : handleShowMap}
                    className="
                        fixed z-[45]
                        right-4
                        top-[var(--nav-fab-offset,10.5rem)]
                        bg-white hover:bg-gray-100
                        text-gray-800
                        px-4 py-2
                        rounded-full shadow-lg
                        flex items-center gap-2
                        transition-all
                        border border-gray-300
                    "
                >
                    {showMap ? (
                        <>
                            <HiOutlineXMark size={20} />
                            <span className="hidden md:block">Hide map</span>
                        </>
                    ) : (
                        <>
                            <HiOutlineMap size={20} />
                            <span className="hidden md:block">Show map</span>
                        </>
                    )}
                </button>
            )}

            {listings.length > 0 ? (
                <div className="relative z-0 flex flex-col md:flex-row gap-6">
                    {showMap && isMobile && (
                        <div className="sticky h-[300px] bg-white">
                            <MapListings
                                listings={visibleListings}
                                currentUser={currentUser}
                                rates={rates}
                            />
                        </div>
                    )}

                    <div className={`
                        w-full
                        ${(!isMobile && showMap) ? 'md:w-2/3' : 'md:w-full'}
                        transition-all duration-300
                    `}>
                        <div className={gridClasses}>
                            {visibleListings.map((listing: SafeListing, index: number) => (
                                locationQuery ? (
                                    <div
                                        key={listing.id}
                                        className="contents"
                                        onClickCapture={() => trackResultClick(listing.id, index)}
                                    >
                                        <ListingCard
                                            currentUser={currentUser}
                                            listing={listing}
                                            rates={rates}
                                        />
                                    </div>
                                ) : (
                                    <ListingCard
                                        currentUser={currentUser}
                                        key={listing.id}
                                        listing={listing}
                                        rates={rates}
                                    />
                                )
                            ))}

                            {isLoading && listings.length > visibleListings.length && (
                                Array(listings.length - visibleListings.length).fill(0).map((_, index) => (
                                    <div key={`skeleton-${index}`} className="col-span-1">
                                        <div className="flex flex-col w-full">
                                            <div className="aspect-square w-full bg-gray-200 rounded-xl animate-pulse"></div>
                                            <div className="h-4 bg-gray-200 rounded mt-2 w-3/4 animate-pulse"></div>
                                            <div className="h-4 bg-gray-200 rounded mt-2 w-1/2 animate-pulse"></div>
                                            <div className="h-4 bg-gray-200 rounded mt-2 w-2/3 animate-pulse"></div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {totalPages > 1 && (
                            <div className="mt-10 mb-6">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={onPageChange}
                                />
                            </div>
                        )}
                    </div>

                    {showMap && !isMobile && (
                        <div className="md:w-1/2 sticky top-24 h-[calc(100vh-6rem)]">
                            <MapListings
                                listings={visibleListings}
                                currentUser={currentUser}
                                rates={rates}
                                isMobile={false}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="w-full text-center py-12">
                    <h2 className="text-xl font-semibold mb-10">
                        No listings found
                    </h2>
                    <button
                        className="px-4 py-2 bg-cyan-700 text-white rounded-md hover:bg-cyan-600 cursor-pointer"
                        onClick={() => router.push('/')}
                    >
                        Remove all filters
                    </button>
                </div>
            )}
        </div>
    );
};

export default SearchClient;
